import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useConstructionProgressDetail } from './hooks/useConstructionProgressDetail';
import { Button, Card, FeaturePlaceholder, Alert, KPICard } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { toast } from 'sonner';
import { ProgressStatusBadge } from './components/ProgressStatusBadge';
import { ProgressPhotoGallery } from './components/ProgressPhotoGallery';
import { ProgressResourceCard } from './components/ProgressResourceCard';

export default function ProgressDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { log: item, photos, isLoading, updateLog } = useConstructionProgressDetail(id);

  const [activeTab, setActiveTab] = useState('overview');

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết nhật ký...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin nhật ký</div>;

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateLog({ status_code: newStatus as any });
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'quantities', label: 'Khối lượng chi tiết' },
    { value: 'photos', label: 'Hình ảnh hiện trường' },
    { value: 'resources', label: 'Nhân công & Thiết bị' },
    { value: 'issues', label: 'Sự cố & Chặn' },
    { value: 'qa', label: 'Kiểm tra QA' },
    { value: 'audit', label: 'Nhật ký thay đổi' },
  ];

  return (
    <PageDetailLayout
      title={`Nhật ký ngày ${new Date(item.log_date).toLocaleDateString('vi-VN')}`}
      description={
        <div className="flex items-center gap-3 mt-1">
          <ProgressStatusBadge status={item.status_code} />
          <span className="text-sm text-slate-400 font-mono">#{item.id.substring(0, 8).toUpperCase()}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-team-line" />
            {item.team?.name || 'Chưa gán tổ đội'}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Nhật ký tiến độ', path: '/construction/progress' },
        { label: 'Chi tiết nhật ký' }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'submitted' && (
            <>
              <AuthButton module="construction" action="approve" variant="success" icon="ri-check-line" onClick={() => handleStatusChange('approved')}>
                Phê duyệt Khối lượng
              </AuthButton>
              <AuthButton module="construction" action="reject" variant="outline-danger" icon="ri-close-line" onClick={() => handleStatusChange('rejected')}>
                Từ chối
              </AuthButton>
            </>
          )}
          <Button variant="outline" icon="ri-printer-line">In phiếu</Button>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Khối lượng hoàn thành" value={item.completed_quantity} icon="ri-line-chart-line" variant="primary" suffix={` ${item.unit}`} />
                <KPICard title="Phần trăm tiến độ" value={item.progress_percent} icon="ri-percent-line" variant="info" suffix="%" />
                <KPICard title="Số ảnh hiện trường" value={photos.length} icon="ri-image-line" variant="warning" />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Tóm tắt nội dung thi công
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-2xl border border-slate-100 italic">
                  "{item.summary || 'Chưa có thông tin tóm tắt nội dung thi công.'}"
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12 mt-6">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Khu vực thực hiện</span>
                    <span className="text-sm font-bold text-os-primary underline">{item.zone?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Điều kiện thời tiết</span>
                    <span className="text-sm font-bold text-slate-700">{item.weather_note || 'Nắng ráo'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Người báo cáo</span>
                    <span className="text-sm font-bold text-slate-700">{item.reporter?.full_name}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Ghi nhận lúc</span>
                    <span className="text-sm font-bold text-slate-700">{new Date(item.created_at).toLocaleString('vi-VN')}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-image-line text-slate-400" />
                  Ảnh hiện trường tiêu biểu
                </h3>
                <ProgressPhotoGallery photos={photos.slice(0, 4)} />
                {photos.length > 4 && (
                  <Button variant="ghost" size="sm" className="w-full mt-4" onClick={() => setActiveTab('photos')}>
                    Xem tất cả {photos.length} ảnh
                  </Button>
                )}
              </Card>
            </div>

            <div className="space-y-6">
              <ProgressResourceCard log={item} />

              {item.issue_note && (
                <Alert variant="warning" title="Ghi chú sự cố" className="text-xs">
                  {item.issue_note}
                </Alert>
              )}

              <Card className="p-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-shield-check-line text-slate-400" />
                  Phê duyệt & Nghiệm thu
                </h4>
                {item.status_code === 'approved' ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-2xl">
                      <p className="text-[10px] text-emerald-600 font-bold  mb-1">Đã phê duyệt bởi</p>
                      <p className="text-xs font-bold text-slate-700">{item.approved_by || 'Giám sát dự án'}</p>
                      <p className="text-[9px] text-slate-400 mt-0.5">{item.approved_at ? new Date(item.approved_at).toLocaleString('vi-VN') : '---'}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic">Nhật ký này đang chờ soát xét và phê duyệt khối lượng.</p>
                )}
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'photos' && (
          <ProgressPhotoGallery photos={photos} />
        )}

        {(activeTab === 'quantities' || activeTab === 'resources' || activeTab === 'issues' || activeTab === 'qa' || activeTab === 'audit') && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Dữ liệu đang được đồng bộ từ nhật ký hiện trường chi tiết, tọa độ GPS ảnh chụp và biên bản nghiệm thu QA kỹ thuật."
          />
        )}
      </div>
    </PageDetailLayout>
  );
}
